// General: nombre, subdominio, contraseña de la web y zona de peligro (despublicar, archivar, borrar).
import { useState, type FormEvent } from 'react';
import { api } from '../../api.ts';
import { t } from '../../i18n/index.ts';
import { navigate } from '../../router.ts';
import { errorText, useApp } from '../../store.ts';
import { Dialog } from '../../ui/Dialog.tsx';
import type { SiteProps } from './SiteShell.tsx';

export function General({ site, onChange }: SiteProps) {
  const me = useApp((s) => s.me);
  const config = useApp((s) => s.config);
  const notify = useApp((s) => s.notify);
  const dropSite = useApp((s) => s.dropSite);
  const [name, setName] = useState(site.name);
  const [slug, setSlug] = useState(site.slug);
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState('');

  const run = async (fn: () => Promise<void>, ok?: string) => {
    try {
      await fn();
      if (ok) notify(ok);
    } catch (e) {
      notify(errorText(e), true);
    }
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => onChange((await api.updateSite(site.id, { name, slug })).site), t('common.saved'));
  };

  return (
    <>
      <section className="section">
        <h2>{t('site.general')}</h2>
        <form className="panel" onSubmit={save}>
          <label className="field-row">
            <span className="label">{t('site.name')}</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required />
          </label>
          <label className="field-row">
            <span className="label">{t('site.subdomain')}</span>
            <div className="row">
              <input className="input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} maxLength={40} required />
              <span className="muted ellipsis" title={`.${config?.baseDomain}`} style={{ flexShrink: 1 }}>
                .{config?.baseDomain}
              </span>
            </div>
          </label>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="btn primary" type="submit" disabled={name === site.name && slug === site.slug}>
              {t('common.save')}
            </button>
          </div>
        </form>
      </section>

      <section className="section">
        <h3>{t('site.passwordProtect')}</h3>
        <p className="hint">{t('site.passwordHint')}</p>
        <div className="panel">
          {site.passwordProtected ? (
            <div className="row">
              <span className="grow">{t('site.passwordOn')}</span>
              <button type="button" className="btn" onClick={() => run(async () => onChange((await api.setPassword(site.id, null)).site), t('site.passwordRemoved'))}>
                {t('site.passwordRemove')}
              </button>
            </div>
          ) : (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => {
                  onChange((await api.setPassword(site.id, password)).site);
                  setPassword('');
                }, t('site.passwordSet'));
              }}
            >
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('site.passwordPlaceholder')} minLength={4} required autoComplete="new-password" />
              <button className="btn" type="submit">
                {t('site.passwordEnable')}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="section">
        <h3>{t('site.dangerZone')}</h3>
        <div className="list">
          <div className="list-row">
            <div className="col grow" style={{ gap: 4 }}>
              <span>{t('site.unpublish')}</span>
              <span className="hint">{t('site.unpublishHint')}</span>
            </div>
            <button type="button" className="btn danger" disabled={!site.published} onClick={() => run(async () => onChange((await api.unpublish(site.id)).site), t('site.unpublished'))}>
              {t('site.unpublish')}
            </button>
          </div>
          <div className="list-row">
            <div className="col grow" style={{ gap: 4 }}>
              <span>{site.archived ? t('projects.unarchive') : t('projects.archive')}</span>
              <span className="hint">{t('site.archiveHint')}</span>
            </div>
            <button type="button" className="btn" onClick={() => run(async () => onChange((await api.updateSite(site.id, { archived: !site.archived })).site))}>
              {site.archived ? t('projects.unarchive') : t('projects.archive')}
            </button>
          </div>
          {site.archived && me?.role === 'owner' ? (
            <div className="list-row">
              <div className="col grow" style={{ gap: 4 }}>
                <span>{t('site.delete')}</span>
                <span className="hint">{t('site.deleteHint')}</span>
              </div>
              <button type="button" className="btn danger" onClick={() => setDeleting(true)}>
                {t('site.delete')}
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {deleting ? (
        <Dialog title={t('site.delete')} onClose={() => setDeleting(false)}>
          <p className="hint">{t('site.deleteConfirm', { slug: site.slug })}</p>
          <input className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={site.slug} autoFocus />
          <div className="dialog-foot">
            <button type="button" className="btn" onClick={() => setDeleting(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn danger"
              disabled={confirm !== site.slug}
              onClick={() =>
                run(async () => {
                  await api.deleteSite(site.id, confirm);
                  dropSite(site.id);
                  navigate('/archive', true);
                }, t('site.deleted'))
              }
            >
              {t('site.delete')}
            </button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
